<template>
  <section class="bulk-import-view">
    <header class="bulk-import-view__hero">
      <div>
        <p class="bulk-import-view__eyebrow">Carga masiva</p>
        <h1 class="bulk-import-view__title">Cuentas bancarias</h1>
        <p class="bulk-import-view__copy">
          Sube un CSV, valida la información, revisa el preview y confirma solo las filas válidas.
        </p>
      </div>

      <div v-if="hasEditPermission" class="bulk-import-view__hero-actions">
        <button class="bulk-import-view__ghost" type="button" :disabled="isDownloadingTemplate" @click="downloadTemplate">
          {{ isDownloadingTemplate ? 'Descargando...' : 'Descargar plantilla CSV' }}
        </button>
      </div>
    </header>

    <div v-if="!hasEditPermission" class="bulk-import-view__empty">
      No tiene permisos para ejecutar cargas masivas.
    </div>

    <template v-else>
      <section class="bulk-import-view__card">
        <div class="bulk-import-view__upload-grid">
          <label class="bulk-import-view__file-field">
            <span class="bulk-import-view__field-label">Archivo CSV</span>
            <input type="file" accept=".csv,text/csv" @change="handleFileChange">
          </label>

          <div class="bulk-import-view__instructions">
            <p class="bulk-import-view__field-label">Reglas</p>
            <div class="bulk-import-view__rule-list">
              <div class="bulk-import-view__rule-item">
                <span class="bulk-import-view__rule-label">Formato</span>
                <span class="bulk-import-view__rule-copy">Solo se admite archivo CSV.</span>
              </div>
              <div class="bulk-import-view__rule-item">
                <span class="bulk-import-view__rule-label">Identificador</span>
                <span class="bulk-import-view__rule-copy">La empresa se resuelve con <code>cliente_rut</code>.</span>
              </div>
              <div class="bulk-import-view__rule-item">
                <span class="bulk-import-view__rule-label">Alcance</span>
                <span class="bulk-import-view__rule-copy">La importación solo crea cuentas nuevas.</span>
              </div>
            </div>
            <div class="bulk-import-view__supported-types">
              <p class="bulk-import-view__supported-title">Tipos de cuenta soportados</p>
              <div class="bulk-import-view__type-chips">
                <span class="bulk-import-view__type-chip">Cuenta Corriente</span>
                <span class="bulk-import-view__type-chip">Cuenta Vista</span>
                <span class="bulk-import-view__type-chip">Cuenta de Ahorro</span>
              </div>
            </div>
          </div>
        </div>

        <div class="bulk-import-view__actions">
          <button class="bulk-import-view__primary" type="button" :disabled="!selectedFile || isUploading" @click="uploadFile">
            {{ isUploading ? 'Validando archivo...' : 'Subir y validar' }}
          </button>
          <button v-if="jobId" class="bulk-import-view__ghost" type="button" @click="startNewJob">
            Nueva carga
          </button>
          <button v-if="jobId" class="bulk-import-view__ghost" type="button" :disabled="isLoadingJob" @click="loadJob">
            {{ isLoadingJob ? 'Actualizando...' : 'Refrescar preview' }}
          </button>
          <button v-if="canCancelJob" class="bulk-import-view__danger" type="button" :disabled="isCancelling" @click="cancelCurrentJob">
            {{ isCancelling ? 'Cancelando...' : 'Cancelar carga' }}
          </button>
        </div>

        <p v-if="selectedFile" class="bulk-import-view__selected-file">
          Archivo seleccionado: {{ selectedFile.name }}
        </p>
        <p v-if="errorMessage" class="bulk-import-view__error">{{ errorMessage }}</p>
        <p v-if="successMessage" class="bulk-import-view__success">{{ successMessage }}</p>
      </section>

      <section v-if="job" class="bulk-import-view__card">
        <div class="bulk-import-view__job-head">
          <div>
            <p class="bulk-import-view__field-label">Carga actual</p>
            <h2 class="bulk-import-view__job-title">{{ job.original_filename }}</h2>
            <p class="bulk-import-view__job-meta">
              Estado: <strong>{{ getJobStatusLabel(job.status) }}</strong> · Filas: {{ job.total_rows }}
            </p>
          </div>

          <div class="bulk-import-view__confirm-actions">
            <button
              class="bulk-import-view__primary"
              type="button"
              :disabled="!canConfirmJob || isConfirming"
              @click="confirmCurrentJob"
            >
              {{ isConfirming ? 'Importando...' : 'Importar filas válidas' }}
            </button>
            <p v-if="confirmButtonHint" class="bulk-import-view__hint">
              {{ confirmButtonHint }}
            </p>
          </div>
        </div>

        <div class="bulk-import-view__summary-grid">
          <article class="bulk-import-view__metric">
            <span class="bulk-import-view__metric-label">Total</span>
            <strong class="bulk-import-view__metric-value">{{ job.total_rows }}</strong>
          </article>
          <article class="bulk-import-view__metric">
            <span class="bulk-import-view__metric-label">Válidas</span>
            <strong class="bulk-import-view__metric-value">{{ job.valid_rows }}</strong>
          </article>
          <article class="bulk-import-view__metric">
            <span class="bulk-import-view__metric-label">Errores</span>
            <strong class="bulk-import-view__metric-value">{{ job.error_rows }}</strong>
          </article>
          <article class="bulk-import-view__metric">
            <span class="bulk-import-view__metric-label">Conflictos</span>
            <strong class="bulk-import-view__metric-value">{{ job.conflict_rows }}</strong>
          </article>
          <article class="bulk-import-view__metric">
            <span class="bulk-import-view__metric-label">Importadas</span>
            <strong class="bulk-import-view__metric-value">{{ job.imported_rows }}</strong>
          </article>
        </div>

        <div class="bulk-import-view__toolbar">
          <div class="bulk-import-view__filters">
            <button
              v-for="option in statusOptions"
              :key="option.value"
              type="button"
              class="bulk-import-view__chip"
              :class="{ 'is-active': statusFilter === option.value }"
              @click="applyStatusFilter(option.value)"
            >
              {{ option.label }}
            </button>
          </div>

          <p class="bulk-import-view__page-copy">
            Mostrando {{ rows.length }} de {{ totalRows }} filas
          </p>
        </div>

        <div v-if="isLoadingRows" class="bulk-import-view__loading">
          Cargando filas...
        </div>

        <div v-else-if="rows.length" class="bulk-import-view__table-wrap">
          <table class="bulk-import-view__table">
            <thead>
              <tr>
                <th>Fila</th>
                <th>Estado</th>
                <th>Empresa</th>
                <th>Banco</th>
                <th>Tipo</th>
                <th>Número</th>
                <th>Titular</th>
                <th>Principal</th>
                <th>Mensajes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.id" :class="getRowToneClass(row)">
                <td>{{ row.row_number }}</td>
                <td>
                  <span class="bulk-import-view__status-pill" :class="getStatusToneClass(getRowStatusTone(row))">
                    {{ getRowStatusLabel(row) }}
                  </span>
                </td>
                <td>
                  <div>{{ row.resolved_company ? row.resolved_company.name : 'Sin empresa' }}</div>
                  <small v-if="row.resolved_company">{{ row.resolved_company.rut }}</small>
                </td>
                <td>{{ row.preview_payload.bank_name || 'Sin banco' }}</td>
                <td>{{ row.preview_payload.account_type || 'Sin tipo' }}</td>
                <td>{{ row.preview_payload.account_number || 'Sin número' }}</td>
                <td>
                  <div>{{ row.preview_payload.account_holder || 'Sin titular' }}</div>
                  <small v-if="row.preview_payload.account_holder_rut">{{ row.preview_payload.account_holder_rut }}</small>
                </td>
                <td>{{ row.preview_payload.is_primary ? 'Sí' : 'No' }}</td>
                <td>
                  <ul class="bulk-import-view__message-list">
                    <li v-for="message in row.messages" :key="`${row.id}-${message.field}-${message.message}`">
                      {{ message.message }}
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="bulk-import-view__empty">
          No hay filas para el filtro seleccionado.
        </div>

        <div v-if="totalPages > 1" class="bulk-import-view__pagination">
          <button class="bulk-import-view__ghost" type="button" :disabled="page <= 1 || isLoadingRows" @click="changePage(page - 1)">
            Anterior
          </button>
          <span>Página {{ page }} de {{ totalPages }}</span>
          <button class="bulk-import-view__ghost" type="button" :disabled="page >= totalPages || isLoadingRows" @click="changePage(page + 1)">
            Siguiente
          </button>
        </div>
      </section>

      <section class="bulk-import-view__card bulk-import-view__card--recent">
        <div class="bulk-import-view__recent-head">
          <div>
            <p class="bulk-import-view__field-label">Cargas recientes</p>
            <p class="bulk-import-view__recent-copy">Últimas 3 cargas para reabrir si hace falta.</p>
          </div>

          <div class="bulk-import-view__recent-actions">
            <button class="bulk-import-view__ghost bulk-import-view__ghost--small" type="button" @click="showRecentJobs = !showRecentJobs">
              {{ showRecentJobs ? 'Ocultar' : 'Mostrar historial' }}
            </button>
            <button
              v-if="showRecentJobs"
              class="bulk-import-view__ghost bulk-import-view__ghost--small"
              type="button"
              :disabled="isLoadingRecentJobs"
              @click="loadRecentJobs"
            >
              {{ isLoadingRecentJobs ? 'Actualizando...' : 'Actualizar' }}
            </button>
          </div>
        </div>

        <div v-if="showRecentJobs">
          <div v-if="isLoadingRecentJobs" class="bulk-import-view__loading">
            Cargando jobs recientes...
          </div>

          <div v-else-if="visibleRecentJobs.length" class="bulk-import-view__table-wrap bulk-import-view__table-wrap--compact">
            <table class="bulk-import-view__table bulk-import-view__table--compact">
              <thead>
                <tr>
                  <th>Archivo</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                  <th>Tot</th>
                  <th>Val</th>
                  <th>Imp</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="recentJob in visibleRecentJobs" :key="recentJob.id">
                  <td>{{ recentJob.original_filename }}</td>
                  <td>
                    <span class="bulk-import-view__status-pill" :class="getStatusToneClass(getJobStatusTone(recentJob.status))">
                      {{ getJobStatusLabel(recentJob.status) }}
                    </span>
                  </td>
                  <td>{{ formatDateTime(recentJob.created_at) }}</td>
                  <td>{{ recentJob.total_rows }}</td>
                  <td>{{ recentJob.valid_rows }}</td>
                  <td>{{ recentJob.imported_rows }}</td>
                  <td>
                    <button
                      type="button"
                      class="bulk-import-view__ghost bulk-import-view__ghost--small"
                      :disabled="isLoadingJob && jobId === recentJob.id"
                      @click="openRecentJob(recentJob)"
                    >
                      {{ jobId === recentJob.id ? 'Actual' : 'Ver' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="bulk-import-view__empty">
            No hay cargas recientes para cuentas bancarias.
          </div>

          <div v-if="recentJobs.length > recentJobsCollapsedCount" class="bulk-import-view__recent-footer">
            <button class="bulk-import-view__ghost bulk-import-view__ghost--small" type="button" @click="showAllRecentJobs = !showAllRecentJobs">
              {{ showAllRecentJobs ? 'Ver menos' : `Ver más (${recentJobs.length - recentJobsCollapsedCount})` }}
            </button>
          </div>
        </div>
      </section>
    </template>
  </section>
</template>

<script>
import auth from '@/auth/index'
import bulkImportService from '@/services/bulkImport.service'

export default {
  name: 'BulkImportBankAccountsView',
  data () {
    return {
      selectedFile: null,
      job: null,
      jobId: 0,
      rows: [],
      totalRows: 0,
      page: 1,
      pageSize: 25,
      statusFilter: '',
      recentJobs: [],
      recentJobsPageSize: 10,
      recentJobsCollapsedCount: 3,
      showRecentJobs: false,
      showAllRecentJobs: false,
      isUploading: false,
      isDownloadingTemplate: false,
      isLoadingJob: false,
      isLoadingRows: false,
      isLoadingRecentJobs: false,
      isConfirming: false,
      isCancelling: false,
      errorMessage: '',
      successMessage: '',
      statusOptions: [
        { value: '', label: 'Todas' },
        { value: 'valid', label: 'Válidas' },
        { value: 'error', label: 'Errores' },
        { value: 'conflict', label: 'Conflictos' }
      ]
    }
  },
  computed: {
    hasEditPermission () {
      return auth.canManageUsers() && auth.hasUserPermission('EDITFORM')
    },
    totalPages () {
      return Math.max(Math.ceil(this.totalRows / this.pageSize), 1)
    },
    validRowsCount () {
      return Number(this.job && this.job.valid_rows ? this.job.valid_rows : 0)
    },
    canConfirmJob () {
      if (!this.job || this.isCompletedJob) return false
      return ['validated', 'failed', 'confirmed'].includes(this.job.status) && this.validRowsCount > 0
    },
    canCancelJob () {
      if (!this.job) return false
      return ['uploaded', 'validated', 'failed', 'confirmed'].includes(this.job.status)
    },
    isCompletedJob () {
      return this.job && ['completed', 'cancelled'].includes(this.job.status)
    },
    visibleRecentJobs () {
      if (this.showAllRecentJobs) return this.recentJobs
      return this.recentJobs.slice(0, this.recentJobsCollapsedCount)
    },
    confirmButtonHint () {
      if (!this.job) return ''
      if (this.isCompletedJob) return 'La carga ya fue completada o cancelada.'
      if (this.validRowsCount <= 0) return 'No hay filas válidas disponibles para importar.'
      if (!['validated', 'failed', 'confirmed'].includes(this.job.status)) {
        return 'La carga todavía no está lista para confirmarse.'
      }
      return ''
    }
  },
  methods: {
    handleFileChange (event) {
      const files = event.target.files || []
      this.selectedFile = files.length ? files[0] : null
      this.errorMessage = ''
      this.successMessage = ''
    },
    async downloadTemplate () {
      this.isDownloadingTemplate = true
      this.errorMessage = ''
      try {
        const response = await bulkImportService.downloadBankAccountsTemplate()
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'text/csv' })
        const fileUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = fileUrl
        link.download = 'plantilla_cuentas_bancarias.csv'
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(fileUrl)
      } catch (error) {
        this.errorMessage = error.message
      } finally {
        this.isDownloadingTemplate = false
      }
    },
    async uploadFile () {
      if (!this.selectedFile) return
      this.isUploading = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const job = await bulkImportService.uploadBankAccountsFile(this.selectedFile)
        this.jobId = job.id
        this.page = 1
        await this.syncRouteQuery()
        await this.loadJob()
        await this.loadRecentJobs()
        this.successMessage = 'Archivo validado correctamente. Revisa el preview antes de confirmar.'
      } catch (error) {
        this.errorMessage = error.message
      } finally {
        this.isUploading = false
      }
    },
    async syncRouteQuery () {
      await this.$router.replace({
        name: 'BulkImportBankAccounts',
        query: this.jobId ? { job: String(this.jobId) } : {}
      }).catch(() => {})
    },
    async loadJob () {
      if (!this.jobId) return
      this.isLoadingJob = true
      this.errorMessage = ''
      try {
        this.job = await bulkImportService.getJob(this.jobId)
        await this.loadRows()
      } catch (error) {
        this.errorMessage = error.message
      } finally {
        this.isLoadingJob = false
      }
    },
    async loadRecentJobs () {
      this.isLoadingRecentJobs = true
      try {
        const response = await bulkImportService.listJobs({
          entity_type: 'bank_accounts',
          page: 1,
          page_size: this.recentJobsPageSize
        })
        this.recentJobs = response.items || []
        if (this.showAllRecentJobs && this.recentJobs.length <= this.recentJobsCollapsedCount) {
          this.showAllRecentJobs = false
        }
      } catch (error) {
        this.errorMessage = error.message
      } finally {
        this.isLoadingRecentJobs = false
      }
    },
    async loadRows () {
      if (!this.jobId) return
      this.isLoadingRows = true
      try {
        const response = await bulkImportService.getJobRows(this.jobId, {
          page: this.page,
          page_size: this.pageSize,
          status: this.statusFilter
        })
        this.rows = response.items || []
        this.totalRows = response.total || 0
      } catch (error) {
        this.errorMessage = error.message
      } finally {
        this.isLoadingRows = false
      }
    },
    async confirmCurrentJob () {
      if (!this.canConfirmJob) return
      this.isConfirming = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        this.job = await bulkImportService.confirmJob(this.jobId)
        await this.loadRows()
        await this.loadRecentJobs()
        this.successMessage = `Carga completada. Se importaron ${this.job.imported_rows} cuentas.`
      } catch (error) {
        this.errorMessage = error.message
      } finally {
        this.isConfirming = false
      }
    },
    async cancelCurrentJob () {
      if (!this.jobId || this.isCompletedJob) return
      this.isCancelling = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        await bulkImportService.cancelJob(this.jobId)
        await this.loadJob()
        await this.loadRecentJobs()
        this.successMessage = 'La carga fue cancelada. Las filas pendientes quedaron omitidas.'
      } catch (error) {
        this.errorMessage = error.message
      } finally {
        this.isCancelling = false
      }
    },
    applyStatusFilter (status) {
      this.statusFilter = status
      this.page = 1
      this.loadRows()
    },
    changePage (page) {
      this.page = page
      this.loadRows()
    },
    getJobStatusLabel (status) {
      const labels = {
        uploaded: 'Subida',
        validated: 'Validada',
        confirmed: 'Confirmada',
        completed: 'Completada',
        failed: 'Fallida',
        cancelled: 'Cancelada'
      }
      return labels[status] || status
    },
    getJobStatusTone (status) {
      if (status === 'completed') return 'valid'
      if (status === 'cancelled' || status === 'failed') return 'error'
      if (status === 'validated' || status === 'confirmed') return 'conflict'
      return 'neutral'
    },
    getRowStatusLabel (row) {
      if (row.import_status === 'imported') return 'Importada'
      if (row.import_status === 'skipped') return 'Omitida'
      if (row.import_status === 'failed') return 'Falló al importar'
      if (row.status === 'valid') return 'Válida'
      if (row.status === 'conflict') return 'Conflicto'
      if (row.status === 'error') return 'Error'
      return row.status
    },
    getRowStatusTone (row) {
      if (row.import_status === 'imported') return 'valid'
      if (row.import_status === 'skipped') return 'neutral'
      if (row.import_status === 'failed') return 'error'
      return row.status || 'neutral'
    },
    getStatusToneClass (status) {
      return `bulk-import-view__status-pill--${status || 'neutral'}`
    },
    getRowToneClass (row) {
      if (row.import_status === 'imported') return 'bulk-import-view__row--imported'
      if (row.import_status === 'skipped') return 'bulk-import-view__row--skipped'
      return `bulk-import-view__row--${row.status || 'neutral'}`
    },
    formatDateTime (value) {
      if (!value) return 'Sin fecha'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString('es-CL')
    },
    async openRecentJob (recentJob) {
      if (!recentJob || !recentJob.id) return
      this.jobId = recentJob.id
      this.page = 1
      this.statusFilter = ''
      this.selectedFile = null
      await this.syncRouteQuery()
      await this.loadJob()
    },
    async startNewJob () {
      this.job = null
      this.jobId = 0
      this.rows = []
      this.totalRows = 0
      this.page = 1
      this.statusFilter = ''
      this.selectedFile = null
      this.errorMessage = ''
      this.successMessage = ''
      await this.syncRouteQuery()
    }
  },
  async mounted () {
    await this.loadRecentJobs()
    const queryJobId = parseInt(this.$route.query.job, 10)
    if (queryJobId) {
      this.jobId = queryJobId
      await this.loadJob()
    }
  }
}
</script>

<style scoped>
.bulk-import-view {
  display: grid;
  gap: 1rem;
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 0 2rem;
}

.bulk-import-view__hero,
.bulk-import-view__card {
  padding: 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 252, 247, 0.92), rgba(246, 239, 231, 0.9));
  box-shadow: 0 18px 42px rgba(43, 34, 24, 0.06);
}

.bulk-import-view__card--recent {
  padding: 0.8rem 1rem;
  background: rgba(255, 252, 247, 0.72);
  box-shadow: 0 10px 24px rgba(43, 34, 24, 0.04);
}

.bulk-import-view__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.bulk-import-view__eyebrow,
.bulk-import-view__field-label {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(58, 46, 33, 0.48);
}

.bulk-import-view__title,
.bulk-import-view__job-title {
  margin: 0;
  color: #211913;
}

.bulk-import-view__copy,
.bulk-import-view__job-meta,
.bulk-import-view__selected-file,
.bulk-import-view__page-copy,
.bulk-import-view__empty,
.bulk-import-view__loading {
  color: rgba(45, 35, 26, 0.68);
}

.bulk-import-view__hero-actions,
.bulk-import-view__actions,
.bulk-import-view__confirm-actions,
.bulk-import-view__pagination,
.bulk-import-view__recent-footer {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.bulk-import-view__recent-head,
.bulk-import-view__recent-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.bulk-import-view__recent-copy {
  margin: 0.15rem 0 0;
  color: rgba(45, 35, 26, 0.6);
  font-size: 0.88rem;
}

.bulk-import-view__recent-footer {
  justify-content: flex-end;
  margin-top: 0.6rem;
}

.bulk-import-view__upload-grid,
.bulk-import-view__summary-grid {
  display: grid;
  gap: 1rem;
}

.bulk-import-view__upload-grid {
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
}

.bulk-import-view__summary-grid {
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin-top: 1rem;
}

.bulk-import-view__metric {
  padding: 0.8rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.55);
}

.bulk-import-view__metric-label {
  display: block;
  color: rgba(58, 46, 33, 0.52);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.bulk-import-view__metric-value {
  color: #241c16;
  font-size: 1.2rem;
}

.bulk-import-view__file-field {
  display: grid;
  gap: 0.5rem;
}

.bulk-import-view__instructions {
  display: grid;
  gap: 0.9rem;
  padding: 1rem 1.05rem;
  border: 1px solid rgba(102, 81, 57, 0.08);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.78) 0%, rgba(250, 246, 240, 0.94) 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

.bulk-import-view__rule-list {
  display: grid;
  gap: 0.55rem;
}

.bulk-import-view__rule-item {
  display: grid;
  gap: 0.18rem;
  padding: 0.6rem 0.7rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.58);
}

.bulk-import-view__rule-label,
.bulk-import-view__supported-title {
  color: rgba(58, 46, 33, 0.72);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.bulk-import-view__rule-copy {
  color: #2f241b;
  line-height: 1.4;
}

.bulk-import-view__supported-types {
  display: grid;
  gap: 0.55rem;
}

.bulk-import-view__supported-title {
  margin: 0;
}

.bulk-import-view__type-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.bulk-import-view__type-chip {
  display: inline-flex;
  align-items: center;
  min-height: 2rem;
  padding: 0.35rem 0.8rem;
  border: 1px solid rgba(102, 81, 57, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  color: #392d22;
  font-size: 0.88rem;
  font-weight: 600;
}

.bulk-import-view__message-list {
  margin: 0;
  padding-left: 1rem;
}

.bulk-import-view__toolbar,
.bulk-import-view__job-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.bulk-import-view__filters {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.bulk-import-view__chip,
.bulk-import-view__primary,
.bulk-import-view__ghost,
.bulk-import-view__danger {
  min-height: 2.6rem;
  padding: 0 0.95rem;
  border-radius: 999px;
}

.bulk-import-view__chip,
.bulk-import-view__ghost {
  border: 1px solid rgba(102, 81, 57, 0.12);
  background: rgba(255, 255, 255, 0.72);
  color: #392d22;
}

.bulk-import-view__ghost--small {
  min-height: 2.2rem;
  padding: 0 0.8rem;
}

.bulk-import-view__chip.is-active,
.bulk-import-view__primary {
  border: 0;
  background: linear-gradient(135deg, #251d16 0%, #4c3c2d 100%);
  color: #f7f0e5;
}

.bulk-import-view__danger {
  border: 0;
  background: rgba(185, 92, 92, 0.18);
  color: #8a3f3f;
}

.bulk-import-view__error,
.bulk-import-view__success,
.bulk-import-view__hint {
  margin: 0.75rem 0 0;
}

.bulk-import-view__error {
  color: #a24444;
}

.bulk-import-view__success {
  color: #2f6a43;
}

.bulk-import-view__hint {
  color: rgba(45, 35, 26, 0.62);
  font-size: 0.88rem;
}

.bulk-import-view__table-wrap {
  margin-top: 1rem;
  overflow: auto;
}

.bulk-import-view__table-wrap--compact {
  margin-top: 0.7rem;
}

.bulk-import-view__table {
  width: 100%;
  border-collapse: collapse;
}

.bulk-import-view__table--compact th,
.bulk-import-view__table--compact td {
  padding: 0.65rem 0.6rem;
  font-size: 0.92rem;
}

.bulk-import-view__table th,
.bulk-import-view__table td {
  padding: 0.8rem 0.7rem;
  border-bottom: 1px solid rgba(95, 76, 52, 0.1);
  text-align: left;
  vertical-align: top;
}

.bulk-import-view__table th {
  color: rgba(58, 46, 33, 0.56);
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.bulk-import-view__status-pill {
  display: inline-flex;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
}

.bulk-import-view__status-pill--valid {
  background: rgba(77, 140, 97, 0.12);
  color: #2f6a43;
}

.bulk-import-view__status-pill--error {
  background: rgba(168, 78, 78, 0.12);
  color: #8a3f3f;
}

.bulk-import-view__status-pill--conflict {
  background: rgba(184, 139, 59, 0.14);
  color: #8a6520;
}

.bulk-import-view__status-pill--neutral {
  background: rgba(120, 110, 102, 0.12);
  color: #655b55;
}

.bulk-import-view__row--valid {
  background: rgba(77, 140, 97, 0.04);
}

.bulk-import-view__row--error {
  background: rgba(168, 78, 78, 0.04);
}

.bulk-import-view__row--conflict {
  background: rgba(184, 139, 59, 0.05);
}

.bulk-import-view__row--imported {
  background: rgba(70, 122, 90, 0.08);
}

.bulk-import-view__row--skipped {
  background: rgba(120, 110, 102, 0.06);
}

@media (max-width: 980px) {
  .bulk-import-view__hero,
  .bulk-import-view__job-head,
  .bulk-import-view__toolbar,
  .bulk-import-view__recent-head {
    flex-direction: column;
  }

  .bulk-import-view__upload-grid,
  .bulk-import-view__summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
